import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

/**
 * Inline confirm dialog for Reject / Hold with an optional reason.
 * Replaces window.confirm — stays inside the page.
 *
 * action: "reject" | "hold"
 */
export function RejectHoldDialog({ open, onOpenChange, taskId, action, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const meta = action === "reject"
    ? {
        title: "Reject task",
        desc: "This closes the task. Rejected tasks are terminal — the agent will stop working on it.",
        confirmLabel: "Reject",
        confirmCls: "bg-rose-600 hover:bg-rose-700 text-white",
        toast: "Task rejected.",
        endpoint: "reject",
      }
    : {
        title: "Hold task",
        desc: "Pauses the task. You can Resume it later to put it back in the previous state.",
        confirmLabel: "Hold",
        confirmCls: "bg-amber-600 hover:bg-amber-700 text-white",
        toast: "Task on hold.",
        endpoint: "hold",
      };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${taskId}/${meta.endpoint}`, { reason: reason.trim() });
      toast.success(meta.toast);
      setReason("");
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent data-testid={`${action}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display">{meta.title}</DialogTitle>
          <DialogDescription>{meta.desc}</DialogDescription>
        </DialogHeader>
        <Textarea
          data-testid={`${action}-reason`}
          placeholder="Reason (optional)…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid={`${action}-cancel`} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid={`${action}-confirm`}
            className={meta.confirmCls}
          >
            {meta.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
